import { isSupabaseConfigured } from './supabaseContent';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY =
  process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY ||
  process.env.REACT_APP_SUPABASE_ANON_KEY;

async function apiGet(path) {
  const response = await fetch(`${SUPABASE_URL}${path}`, {
    cache: 'no-store',
    headers: {
      apikey: SUPABASE_KEY,
      Authorization: `Bearer ${SUPABASE_KEY}`
    }
  });

  if (!response.ok) {
    const message = await response.text();
    throw new Error(`Supabase request failed: ${response.status} ${message}`);
  }

  return response.json();
}

function normalizeInteraction(value) {
  if (!value) return null;
  if (typeof value === 'object') return value;
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }
  return null;
}

function normalizeQuizOptions(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

export async function fetchLessonWithStepsBySlug(slug, language) {
  if (!isSupabaseConfigured()) return null;

  const lessons = await apiGet(
    `/rest/v1/lessons?slug=eq.${encodeURIComponent(slug)}&is_published=eq.true&select=id,module_id,slug,title_ro,title_en,description_ro,description_en,lesson_type,duration_min`
  );
  const lesson = lessons[0];
  if (!lesson) return null;

  const steps = await apiGet(
    `/rest/v1/lesson_steps?lesson_id=eq.${lesson.id}&order=step_order.asc&select=id,step_order,fen,text_ro,text_en,audio_url,audio_url_ro,audio_url_en,video_url,interaction`
  );

  const quizzes = await apiGet(
    `/rest/v1/lesson_quizzes?lesson_id=eq.${lesson.id}&order=id.asc&limit=1&select=id,title_ro,title_en,pass_score`
  );
  const quiz = quizzes[0] || null;
  let quizQuestions = [];
  if (quiz?.id) {
    quizQuestions = await apiGet(
      `/rest/v1/quiz_questions?quiz_id=eq.${quiz.id}&order=sort_order.asc&select=id,question_ro,question_en,options,correct_option_id,explanation_ro,explanation_en,sort_order`
    );
  }

  return {
    id: lesson.slug,
    lessonDbId: lesson.id,
    moduleDbId: lesson.module_id || null,
    title: language === 'ro' ? lesson.title_ro : lesson.title_en,
    description: language === 'ro' ? lesson.description_ro : lesson.description_en,
    durationMin: lesson.duration_min,
    lessonType: lesson.lesson_type,
    quiz: quiz
      ? {
          id: quiz.id,
          title: language === 'ro' ? quiz.title_ro : quiz.title_en,
          passScore: Number(quiz.pass_score || 0),
          questions: quizQuestions.map((q) => ({
            id: q.id,
            question: language === 'ro' ? q.question_ro : q.question_en,
            options: normalizeQuizOptions(q.options).map((opt) => ({
              id: String(opt?.id || ''),
              text: language === 'ro' ? String(opt?.text_ro || '') : String(opt?.text_en || '')
            })),
            correctOptionId: String(q.correct_option_id || ''),
            explanation: language === 'ro' ? q.explanation_ro : q.explanation_en,
            sortOrder: Number(q.sort_order || 0)
          }))
        }
      : null,
    segments: steps.map((step) => ({
      // Prefer current language audio, then fallback to the other language,
      // then legacy single audio_url.
      // This avoids silent steps when only one language URL is populated.
      // Trim to avoid accidental whitespace-only values.
      _audioPreferred: (language === 'ro' ? step.audio_url_ro : step.audio_url_en),
      _audioOther: (language === 'ro' ? step.audio_url_en : step.audio_url_ro),
      id: step.step_order,
      stepId: step.id,
      fen: step.fen,
      text: language === 'ro' ? step.text_ro : step.text_en,
      audioUrl: (
        (language === 'ro' ? step.audio_url_ro : step.audio_url_en) ||
        (language === 'ro' ? step.audio_url_en : step.audio_url_ro) ||
        step.audio_url ||
        ''
      ).trim() || null,
      videoUrl: step.video_url,
      interaction: normalizeInteraction(step.interaction),
      moves: []
    })).map(({ _audioPreferred, _audioOther, ...seg }) => seg)
  };
}
